import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { leaveAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const LeaveManagement = () => {
  const { currentUser, hasPermission } = useAuth();
  const isPrivileged = hasPermission('employee_view');
  const [showModal, setShowModal] = useState(false);
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState({ casual: {}, sick: {}, annual: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ employee: '', leaveType: 'casual', startDate: '', endDate: '', reason: '' });

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const res = await leaveAPI.getAll();
      setLeaves(res?.data || res || []);
      if (currentUser?._id) {
        const bal = await leaveAPI.getBalance(currentUser._id).catch(() => null);
        if (bal?.data) setBalance(bal.data);
      }
    } catch (e) { setError('Failed to load leaves'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLeaves(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!isPrivileged) payload.employee = currentUser._id;
      await leaveAPI.apply(payload);
      setShowModal(false);
      setForm({ employee: '', leaveType: 'casual', startDate: '', endDate: '', reason: '' });
      fetchLeaves();
    } catch (err) { setError('Failed to apply leave'); }
  };

  const handleStatus = async (id, status) => {
    try { await leaveAPI.updateStatus(id, { status }); fetchLeaves(); }
    catch (e) { setError('Failed to update status'); }
  };

  const totalRemaining = Object.values(balance).reduce((s, v) => s + (v.remaining || 0), 0);
  const totalUsed = Object.values(balance).reduce((s, v) => s + (v.used || 0), 0);
  const totalLeave = Object.values(balance).reduce((s, v) => s + (v.total || 0), 0);
  const pendingCount = leaves.filter(l => l.status === 'pending').length;

  const leaveData = leaves;

  return (
    <div className="leave-management">
      <div className="module-header">
        <h2>Leave Management</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <i className="fas fa-plus"></i> Apply Leave
        </button>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      <div className="leave-stats">
        <div className="leave-stat"><h4>Total Leave</h4><p>{totalLeave} days</p></div>
        <div className="leave-stat"><h4>Used</h4><p>{totalUsed} days</p></div>
        <div className="leave-stat"><h4>Remaining</h4><p>{totalRemaining} days</p></div>
        <div className="leave-stat"><h4>Pending Approval</h4><p>{pendingCount} requests</p></div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p>Loading...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {isPrivileged && <th>Employee</th>}
                <th>Leave Type</th>
                <th>From Date</th>
                <th>To Date</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaveData.length === 0
                ? <tr><td colSpan="8" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No leave records found</td></tr>
                : leaveData.map(leave => (
                  <tr key={leave._id}>
                    {isPrivileged && <td>{leave.employee?.name || '—'}</td>}
                    <td style={{ textTransform: 'capitalize' }}>{leave.leaveType}</td>
                    <td>{leave.startDate ? new Date(leave.startDate).toLocaleDateString() : '—'}</td>
                    <td>{leave.endDate ? new Date(leave.endDate).toLocaleDateString() : '—'}</td>
                    <td>{leave.days || '—'}</td>
                    <td>{leave.reason}</td>
                    <td>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                        background: leave.status === 'approved' ? '#d1fae5' : leave.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                        color: leave.status === 'approved' ? '#065f46' : leave.status === 'rejected' ? '#991b1b' : '#92400e'
                      }}>{leave.status}</span>
                    </td>
                    <td>
                      {isPrivileged && leave.status === 'pending' ? (
                        <>
                          <button className="btn-small btn-success" onClick={() => handleStatus(leave._id, 'approved')}>Approve</button>
                          <button className="btn-small btn-danger" onClick={() => handleStatus(leave._id, 'rejected')}>Reject</button>
                        </>
                      ) : (
                        <button className="btn-icon view"><i className="fas fa-eye"></i></button>
                      )}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onClose={() => setShowModal(false)} title="Apply for Leave">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Leave Type</label>
            <select className="form-input" value={form.leaveType} onChange={e => setForm({ ...form, leaveType: e.target.value })} required>
              <option value="casual">Casual Leave</option>
              <option value="sick">Sick Leave</option>
              <option value="annual">Annual Leave</option>
              <option value="maternity">Maternity Leave</option>
              <option value="paternity">Paternity Leave</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>From Date</label>
              <input type="date" className="form-input" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <input type="date" className="form-input" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} required />
            </div>
          </div>
          <div className="form-group">
            <label>Reason</label>
            <textarea className="form-input" rows="3" placeholder="Enter reason..." value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} required></textarea>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Apply Leave</button>
          </div>
        </form>
      </Modal>

      <style jsx>{`
        .leave-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }

        .leave-stat {
          background: white;
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .leave-stat h4 {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 10px;
        }

        .leave-stat p {
          font-size: 28px;
          font-weight: 700;
          color: var(--primary-color);
        }

        .btn-small {
          padding: 6px 12px;
          font-size: 12px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          margin-right: 5px;
        }

        .btn-success {
          background: var(--success-color);
          color: white;
        }

        .btn-danger {
          background: var(--danger-color);
          color: white;
        }
      `}</style>
    </div>
  );
};

export default LeaveManagement;
